import { ActionIcon, Grid, Group, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { IconMapPin, IconNote, IconPlus, IconTrash } from '@tabler/icons-react';
import classes from './PartnerEditDrawer.module.css';
import { Organization } from '@medplum/fhirtypes';
import { removeOrgAddressAt, removeOrgTelecomAt, inferSystem } from '../../services/organization';
import { SystemIcon } from './SystemIcon';
import { SmartEditableField, EditableField } from '@/components/EditableField';

export function PartnerAboutSection({
  organization,
  onChange,
}: {
  organization: Organization;
  onChange: (org: Organization) => void;
}) {
  const telecom = organization.telecom || [];
  const addresses = organization.address || [];

  // Add/edit state for telecom and addresses separately
  const [addingNewTelecom, setAddingNewTelecom] = useState(false);
  const [newTelecomValue, setNewTelecomValue] = useState('');
  const [addingNewAddress, setAddingNewAddress] = useState(false);
  const [newAddressValue, setNewAddressValue] = useState('');
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});

  const deleteTelecomAt = async (index: number) => {
    onChange(removeOrgTelecomAt(organization, index));
  };

  const deleteAddressAt = async (index: number) => {
    onChange(removeOrgAddressAt(organization, index));
  };

  const addNewTelecom = async (value: string) => {
    const v = value.trim();
    if (!v) return;
    const system = inferSystem(v) ?? 'other';
    const nextTelecom = [...telecom];
    nextTelecom.push({ system: system as any, value: v, rank: nextTelecom.length + 1 });
    onChange({ ...organization, telecom: nextTelecom, address: addresses });
    setAddingNewTelecom(false);
  };

  const addNewAddress = async (value: string) => {
    const v = value.trim();
    if (!v) return;
    const nextAddresses = [...addresses];
    nextAddresses.push({ text: v } as any);
    onChange({ ...organization, telecom, address: nextAddresses });
    setAddingNewAddress(false);
  };

  // Edit handlers that do NOT move entries across sections
  const editTelecomAt = async (index: number, val: string, key: string) => {
    const inferred = inferSystem(val);
    const nextTelecom = [...telecom];
    const prev = nextTelecom[index];
    nextTelecom[index] = {
      ...prev,
      system: (inferred ?? prev.system ?? 'other') as any,
      value: val,
    } as any;
    onChange({ ...organization, telecom: nextTelecom, address: addresses });
    setEditingValues((prevState) => {
      const { [key]: _omit, ...rest } = prevState;
      return rest;
    });
  };

  const editAddressAt = async (index: number, val: string, key: string) => {
    const nextAddresses = [...addresses];
    nextAddresses[index] = { ...nextAddresses[index], text: val } as any;
    onChange({ ...organization, telecom, address: nextAddresses });
    setEditingValues((prevState) => {
      const { [key]: _omit, ...rest } = prevState;
      return rest;
    });
  };

  return (
    <Stack gap="xs">
      {/* Telecom section */}
      <Group w="100%" justify="space-between">
        <Text style={{ textTransform: 'uppercase' }} c="gray">
          Contact Info
        </Text>
        <ActionIcon onClick={() => setAddingNewTelecom((v) => !v)} variant="subtle" color="gray" size={12} radius="md">
          <IconPlus />
        </ActionIcon>
      </Group>

      {telecom.map((t, index) => {
        const key = `telecom-${index}`;
        const value = editingValues[key] ?? t.value ?? '';
        return (
          <Group key={key} w="100%" justify="space-between" className={classes.aboutRow}>
            <Group style={{ flex: 1, minWidth: 0 }} gap="xs" wrap="nowrap" my="auto">
              <SystemIcon system={inferSystem(value) ?? t.system} value={value} fallback={IconNote} />
              <SmartEditableField
                value={t.value ?? ''}
                placeholder={`Set ${t.system ?? 'value'}`}
                inputProps={{ size: 'xs' }}
                textProps={{ flex: 1 }}
                editable={true}
                dynamicSystem={true}
                onChange={(val) => setEditingValues((prev) => ({ ...prev, [key]: val }))}
                onSubmit={async (val) => editTelecomAt(index, val, key)}
              />
            </Group>
            <ActionIcon
              onClick={() => deleteTelecomAt(index)}
              variant="subtle"
              color="red"
              size={16}
              radius="md"
              className={classes.showOnHover}
            >
              <IconTrash />
            </ActionIcon>
          </Group>
        );
      })}

      {addingNewTelecom && (
        <Grid w="100%">
          <Grid.Col span={1} my="auto">
            <SystemIcon system={inferSystem(newTelecomValue)} value={newTelecomValue} fallback={IconNote} />
          </Grid.Col>
          <Grid.Col span={11}>
            <SmartEditableField
              value={newTelecomValue}
              placeholder="Enter phone, email, website, etc."
              inputProps={{ size: 'xs', w: '90%' }}
              editable={true}
              focus={addingNewTelecom}
              dynamicSystem={true}
              onChange={(v) => setNewTelecomValue(v)}
              onSubmit={async (v) => {
                await addNewTelecom(v);
                setNewTelecomValue('');
              }}
              onCancel={() => {
                setAddingNewTelecom(false);
                setNewTelecomValue('');
              }}
              onEndEditing={(info) => {
                if (!info.value) {
                  setAddingNewTelecom(false);
                  setNewTelecomValue('');
                }
              }}
            />
          </Grid.Col>
        </Grid>
      )}

      {/* Addresses section */}
      <Group w="100%" justify="space-between" mt="sm">
        <Text style={{ textTransform: 'uppercase' }} c="gray">
          Addresses
        </Text>
        <ActionIcon onClick={() => setAddingNewAddress((v) => !v)} variant="subtle" color="gray" size={12} radius="md">
          <IconPlus />
        </ActionIcon>
      </Group>

      {addresses.map((a, index) => {
        const key = `address-${index}`;
        const value = editingValues[key] ?? (a as any).text ?? '';
        return (
          <Group key={key} w="100%" justify="space-between" className={classes.aboutRow}>
            <Group style={{ flex: 1, minWidth: 0 }} gap="xs" wrap="nowrap">
              <SystemIcon system={undefined} value={value} fallback={IconMapPin} />
              <EditableField
                value={(a as any).text ?? ''}
                placeholder={'Set address'}
                inputProps={{ size: 'xs' }}
                textProps={{ flex: 1 }}
                editable={true}
                onChange={(val) => setEditingValues((prev) => ({ ...prev, [key]: val }))}
                onSubmit={async (val) => editAddressAt(index, val, key)}
              />
            </Group>
            <ActionIcon
              onClick={() => deleteAddressAt(index)}
              variant="subtle"
              color="red"
              size={16}
              radius="md"
              className={classes.showOnHover}
            >
              <IconTrash />
            </ActionIcon>
          </Group>
        );
      })}

      {addingNewAddress && (
        <Grid w="100%">
          <Grid.Col span={1}>
            <SystemIcon system={undefined} value={newAddressValue} fallback={IconMapPin} />
          </Grid.Col>
          <Grid.Col span={11}>
            <EditableField
              value=""
              placeholder="Enter address"
              inputProps={{ size: 'xs', w: '90%' }}
              editable={true}
              focus={addingNewAddress}
              onChange={(v) => setNewAddressValue(v)}
              onSubmit={async (v) => {
                await addNewAddress(v);
                setNewAddressValue('');
              }}
              onCancel={() => {
                setAddingNewAddress(false);
                setNewAddressValue('');
              }}
              onEndEditing={(info) => {
                if (!info.value) {
                  setAddingNewAddress(false);
                  setNewAddressValue('');
                }
              }}
            />
          </Grid.Col>
        </Grid>
      )}
    </Stack>
  );
}
